import {Popup} from "@bryntum/schedulerpro";
import {
  editorContextMenuConfig,
  handleArchiveContextMenuItem,
  handleCopyContextMenuItem,
  handleDeleteContextMenuItem,
  handleShareContextMenuItem,
  handleViewAgendaContextMenuItem
} from "../../../other/utils/editorUtils.jsx";
import {EDITOR_CONTEXT_MENU_ITEM_KEYS} from "../../../other/constants/OtherKeys.jsx";

class EditorBase extends Popup {
  static get type () {
    return "editorbase"
  }

  static get configurable() {
    return {
      statefulEvents: ['resize'],
    }
  }

  /* This override brings the current editor to the front and sends all other editors behind it.
  Bug: When switching browser tabs, the `toFront` function was being called unnecessarily for all editors, causing the first opened editor to come to the front.
  In the `Widget` base class, the `onFocusIn` function was responsible for this behavior.
  By overriding `toFront` here, we check if the call is triggered by a user action—such as clicking the editor header or opening an existing editor.
  This prevents unnecessary invocations and fixes the bug.
  See usage in `useEditorHandler` and `SwivuModal`. */
  toFront(canDo) {
    if (canDo) {
      super.toFront();
    }
  }

  doConfigureEditorContextMenu(record, context, configOptions) {
    if (!this.tools.contextMenu) this.tools.contextMenu = editorContextMenuConfig;
    const { contextMenu } = this.widgetMap;
    contextMenu.swivuRecord = record;
    const contextMenuWidgetMap = contextMenu.menu.widgetMap;
    const configOpt = {...configOptions, ths: this};
    const contextMenuOptions = {
      [EDITOR_CONTEXT_MENU_ITEM_KEYS.archiveItem]: handleArchiveContextMenuItem,
      [EDITOR_CONTEXT_MENU_ITEM_KEYS.deleteItem]: handleDeleteContextMenuItem,
      [EDITOR_CONTEXT_MENU_ITEM_KEYS.shareItem]: handleShareContextMenuItem,
      [EDITOR_CONTEXT_MENU_ITEM_KEYS.viewAgendaItem]: handleViewAgendaContextMenuItem,
      [EDITOR_CONTEXT_MENU_ITEM_KEYS.copyItem]: handleCopyContextMenuItem
    }

    Object.entries(contextMenuOptions).forEach(([contextMenuItemKey, itemHandler]) => {
      if (configOpt.items?.[contextMenuItemKey]?.enabled) {
        contextMenuWidgetMap[contextMenuItemKey].hidden = false;
        itemHandler(contextMenuWidgetMap, context, record, configOpt);
      } else {
        contextMenuWidgetMap[contextMenuItemKey].hidden = true;
      }
    });

    // add new items provided by child to context menu
    if (configOptions.newMenuItems && typeof configOptions.newMenuItems === "object") {
      Object.entries(configOptions.newMenuItems).forEach(([newItemKey, newItemConfig]) => {
        if (!contextMenuWidgetMap[newItemKey]) {
          contextMenu.menu.add({ ...newItemConfig, ref: newItemKey });
        } else {
          contextMenuWidgetMap[newItemKey].disabled = false;
        }
      });
    }

    contextMenu.hidden = !contextMenu.menu.items.find((item) => !item.hidden);
  }

  doConfigureEditor(record, context, configOptions) {
    this.doConfigureEditorContextMenu(record, context, configOptions);
  }

  configureEditorContextMenu(record, context, configOptions) {
    this.doConfigureEditor(record, context, configOptions);
    // once phantom record is created we need to reconfigure editor context menu to re-enable/disable items
    if (record.isPhantom) {
      // in case if editor re-render the old listener needs to be destroyed
      this.destroyTabCheckListener();
      this.swivuHiddenTabsCheckListener = configOptions.recordStore.on({
        idChange({ record: persistedRecord }) {
          if (persistedRecord.id === record.id) {
            this.doConfigureEditor(record, context, configOptions)
            this.destroyTabCheckListener();
          }
        },
        thisObj: this,
      });
    }
  }

  destroyTabCheckListener() {
    if (this.swivuHiddenTabsCheckListener) {
      this.swivuHiddenTabsCheckListener();
      this.swivuHiddenTabsCheckListener = null;
    }
  }

  saveState() {
    if (!this.stateId || !this.stateProvider.isSwivuStateProvider) return;
    this.stateProvider.setValue(this.stateId, { style: { localStoragePersistedStyles: `width: ${this.width}px; height: ${this.height}px` } });
  }

  editRecord(_record, context, otherParams) {
    if (otherParams.isFirstShow) {
      this.toFront(true);
    }
    if (context.user) {
      this.swivuCheckGrants(context.user);
    }
    if (this.stateId && context.stateProvider) {
      const state = context.stateProvider.getValue(this.stateId);
      if (state && state.width && state.height) {
        this.width = state.width;
        this.height = state.height;
      }
      this.stateProvider = context.stateProvider;
    }
  }

  swivuCheckGrants()  {
  }

  changeCrossToEsc() {
    if (this.headerElement) {
      const headerCloseElement = this.headerElement.querySelector(".b-popup-close");
      if (headerCloseElement) {
        headerCloseElement.innerText = "ESC"
        headerCloseElement.classList.add("header-close-esc-button");
      }
    }

    if (this.tools.contextMenu) {
      this.tools.contextMenu.element.classList.add("swivu-editor-header-context-button");
    }
  }

  swivuSetTitleLineThrough() {
    if (this.titleElement) {
      this.titleElement.style.textDecoration = "line-through";
    }
  }
}

export default EditorBase;
